import React from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Avatar,
  Text,
  Button,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  useToast,
} from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Ban, Unlock, ArrowLeft, MapPin } from 'lucide-react';
import AppHeader from '@/components/navigation/AppHeader';
import { useGetBlockedUsers, useUnblockUser } from '@/api';

function BlockedUserCard({
  blockedUser,
  onUnblock,
  isUnblocking,
}: {
  blockedUser: any;
  onUnblock: (userId: number) => void;
  isUnblocking: boolean;
}) {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate({ to: '/profile/$userId', params: { userId: String(blockedUser.blocked.id) } });
  };

  const handleUnblockClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUnblock(blockedUser.blocked.id);
  };

  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      _hover={{ boxShadow: 'md', cursor: 'pointer' }}
      transition="all 0.2s"
      onClick={handleCardClick}
    >
      <HStack spacing={4} align="start">
        <Avatar
          size="lg"
          name={blockedUser.blocked.name}
          src={blockedUser.blocked.profilePicture || undefined}
        />
        <VStack align="start" flex={1} spacing={1}>
          <Text
            fontSize="md"
            fontWeight="bold"
            color={'gray.700'}
            _hover={{ textDecoration: 'underline' }}
          >
            {blockedUser.blocked.name}
          </Text>
          {blockedUser.blocked.location && (
            <HStack>
              <MapPin size={16} color="var(--color-purple-300)" />
              <Text fontSize="sm" color="gray.600">
                {blockedUser.blocked.location}
              </Text>
            </HStack>
          )}
          {blockedUser.blocked.bio && (
            <Text fontSize="sm" color="gray.700" noOfLines={2}>
              {blockedUser.blocked.bio}
            </Text>
          )}
          <Text fontSize="xs" color="gray.500">
            Blocked {formatDistanceToNow(new Date(blockedUser.createdAt), { addSuffix: true })}
          </Text>
          {blockedUser.reason && (
            <Text fontSize="xs" color="red.600" fontStyle="italic">
              Reason: {blockedUser.reason}
            </Text>
          )}
        </VStack>
        <Button
          size="sm"
          bg="purple.500"
          color="white"
          _hover={{ bg: 'purple.600' }}
          leftIcon={<Unlock size={16} />}
          onClick={handleUnblockClick}
          isLoading={isUnblocking}
        >
          Unblock
        </Button>
      </HStack>
    </Box>
  );
}

function BlockedUserSkeleton() {
  return (
    <Box p={4} bg="white" borderRadius="lg" borderWidth="1px">
      <HStack spacing={4}>
        <SkeletonCircle size="12" />
        <VStack align="start" flex={1} spacing={2}>
          <Skeleton height="20px" width="150px" />
          <Skeleton height="16px" width="200px" />
          <SkeletonText mt={2} noOfLines={2} spacing={2} skeletonHeight="14px" width="100%" />
        </VStack>
        <Skeleton height="32px" width="100px" />
      </HStack>
    </Box>
  );
}

export function BlockedUsers() {
  const toast = useToast();
  const router = useRouter();
  const [unblockingUserId, setUnblockingUserId] = React.useState<number | null>(null);

  const { data: blockedUsers = [], isLoading } = useGetBlockedUsers();
  const { mutateAsync: unblockUser } = useUnblockUser();

  const handleUnblock = async (userId: number) => {
    setUnblockingUserId(userId);
    try {
      await unblockUser(userId);
      toast({
        title: 'Member unblocked',
        description: 'You can now interact with this member again',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: 'Failed to unblock member',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUnblockingUserId(null);
    }
  };

  return (
    <PageWrapper>
      <SEO title="Blocked Members" />
      <AppHeader page={'blocked-users'} />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="center" px={2}>
              <Button
                leftIcon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={() => router.history.back()}
                size={{ base: 'sm', lg: 'md' }}
                color="white"
                _hover={{ bg: 'whiteAlpha.300' }}
                minH={'44px'}
                minW={'44px'}
              >
              </Button>
              <VStack align="center" spacing={0}>
                <Heading size="lg" color="white">Blocked Members</Heading>
                <Text fontSize="sm" color="gray.400">
                  Manage blocked accounts
                </Text>
              </VStack>
              <Box w="60px" /> {/* Spacer for center alignment */}
            </HStack>

            {/* Blocked Users List */}
            <VStack spacing={4} align="stretch">
              {isLoading ? (
                <>
                  <BlockedUserSkeleton />
                  <BlockedUserSkeleton />
                  <BlockedUserSkeleton />
                </>
              ) : blockedUsers.length === 0 ? (
                <Box
                  p={12}
                  bg="white"
                  borderRadius="lg"
                  textAlign="center"
                  borderWidth="1px"
                  borderStyle="dashed"
                >
                  <Ban size={48} style={{ margin: '0 auto 16px', color: 'var(--color-gray-300)' }} />
                  <Text fontSize="lg" color="gray.600" mb={2}>
                    No blocked members
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Members you block will appear here
                  </Text>
                </Box>
              ) : (
                blockedUsers.map((blockedUser) => (
                  <BlockedUserCard
                    key={blockedUser.id}
                    blockedUser={blockedUser}
                    onUnblock={handleUnblock}
                    isUnblocking={unblockingUserId === blockedUser.blocked.id}
                  />
                ))
              )}
            </VStack>
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
